import { Language } from '@/types'

// ============================================================================
// RTL (Right-to-Left) Utilities
// ============================================================================

/**
 * Check if a language is RTL
 */
export function isRTL(lang: Language): boolean {
  return lang === 'ar'
}

/**
 * Get the text direction for a language
 */
export function getTextDirection(lang: Language): 'rtl' | 'ltr' {
  return isRTL(lang) ? 'rtl' : 'ltr'
}

/**
 * Apply RTL-specific class names
 */
export function getRTLClass(
  lang: Language,
  ltrClass: string,
  rtlClass?: string
): string {
  if (isRTL(lang) && rtlClass) {
    return rtlClass
  }
  return ltrClass
}

/**
 * Get margin/padding classes that flip for RTL
 * Left becomes right and vice versa
 */
export function getFlipClass(lang: Language, side: 'left' | 'right'): string {
  const isArabic = isRTL(lang)
  if (side === 'left') {
    return isArabic ? 'rtl' : 'ltr'
  }
  return isArabic ? 'ltr' : 'rtl'
}

/**
 * Get flex direction for RTL (reverses row direction)
 */
export function getFlexDirection(lang: Language): 'row' | 'row-reverse' {
  return isRTL(lang) ? 'row-reverse' : 'row'
}

/**
 * Check if an animation should be reversed for RTL
 */
export function shouldReverseAnimation(lang: Language): boolean {
  return isRTL(lang)
}

/**
 * Get the appropriate scroll behavior for a language
 */
export function getScrollBehavior(lang: Language): {
  direction: 'ltr' | 'rtl'
  scrollLeft: number
} {
  return {
    direction: getTextDirection(lang),
    scrollLeft: isRTL(lang) ? -1 : 0,
  }
}

/**
 * Get text alignment class for a language
 */
export function getTextAlign(lang: Language): string {
  return isRTL(lang) ? 'text-right' : 'text-left'
}

/**
 * Mirror an icon for RTL if needed
 * Some icons like arrows should be mirrored in RTL
 */
export function shouldMirrorIcon(lang: Language, iconType: 'arrow' | 'chevron' | 'nav'): boolean {
  if (!isRTL(lang)) {
    return false
  }

  // These icon types should be mirrored in RTL
  return ['arrow', 'chevron', 'nav'].includes(iconType)
}

/**
 * Get icon transform for RTL
 */
export function getIconTransform(lang: Language, iconType: 'arrow' | 'chevron' | 'nav'): string {
  if (shouldMirrorIcon(lang, iconType)) {
    return 'scaleX(-1)'
  }
  return 'none'
}
